import { MAIN_SAFE, MODULE_SAFE } from './constants.js'

/**
 * Resolves the user's `?safe=` choice into one of three states:
 *
 *  - `landing`   — no input. Bare URL = public landing page; we ask the
 *                  visitor for their Safe.
 *  - `safeward`  — input matches the Module Safe or Main Safe of the
 *                  deployed safeward pair → render the dashboard.
 *  - `onboarding`— input is some other address (or unparseable) → tell the
 *                  visitor safeward isn't enabled on their Safe yet, with
 *                  step-by-step instructions.
 *
 * v1 only knows the one safeward deployment, so this is string matching.
 * When safeward goes multi-tenant, this is where the on-chain
 * `getModulesPaginated` probe lands.
 */
export type ResolvedSafe =
  | { kind: 'landing' }
  | {
      kind: 'safeward'
      /** What we feed to /v1/pending — always the Module Safe. */
      moduleSafe: string
      /** The protected wallet, used for Safe{Wallet} links + header pills. */
      mainSafe: string
    }
  | {
      kind: 'onboarding'
      /** The address the visitor passed. */
      input: string
    }

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/

export function resolveSafe(input: string | null | undefined): ResolvedSafe {
  const trimmed = (input ?? '').trim()
  if (!trimmed) return { kind: 'landing' }

  // We only persist 40-hex addresses (storage.ts strips anything else from
  // ?safe=), so a non-empty non-address here means "user wrote something
  // weird" — surface as onboarding so they get directed to a real Safe.
  if (!ADDRESS_RE.test(trimmed)) {
    return { kind: 'onboarding', input: trimmed }
  }
  const norm = trimmed.toLowerCase()
  if (
    norm === MAIN_SAFE.toLowerCase() ||
    norm === MODULE_SAFE.toLowerCase()
  ) {
    return { kind: 'safeward', moduleSafe: MODULE_SAFE, mainSafe: MAIN_SAFE }
  }
  return { kind: 'onboarding', input: trimmed }
}

/**
 * Extracts a Safe address from arbitrary user input. Accepts:
 *  - a raw `0x…` address
 *  - a Safe{Wallet} URL (`https://app.safe.global/...?safe=eth:0x…`)
 *  - any string containing a 40-hex address
 *
 * Returns null when no address can be located.
 */
export function parseSafeInput(raw: string): string | null {
  const match = raw.match(/0x[0-9a-fA-F]{40}/)
  return match ? match[0] : null
}
